import { AgTechDataAction, AgTechDataActionSuccessfulExecutionHandlingProps } from "@agtech/core/data/actions/AgTechDataActions";
import { useAgTechHttpDeleteEntityRequest } from "@agtech/core/data/http/AgTechApiDeleteRequests";
import { useAgTechHttpSubmitEntityRequest } from "@agtech/core/data/http/AgTechApiPostRequests";
import { DefaultAgTechConfirmationActions } from "@agtech/core/functions/AgTechAppConfirmations";
import { ContactCredit } from "./ContactCreditData";
import { useAgTechPopUpContext } from "@agtech/core/functions/AgTechAppPopUps";
import { SaleContactDataGetContactDetailsQuery } from "../SaleContactDataQueries";
import { Index } from "@agtech/core/utilities/AgTechUtilities";
import { InvoiceDataGetInvoiceDocumentQuery } from "../../../invoicing/InvoicingDataQueries";

export const useContactCreditDataWriteCreditAction = (
    contactId: number,
    onSuccess?: (props: AgTechDataActionSuccessfulExecutionHandlingProps<ContactCredit, ContactCredit>) => Promise<void>
): AgTechDataAction<ContactCredit, ContactCredit> => {
    const contactCreditSubmissionRequest = useAgTechHttpSubmitEntityRequest<ContactCredit>();

    return {
        name: 'WriteCredit',
        getConfiguration: props => ({
            actionExecutionMessage: props.originalEntity?.id ? 'Updating credit...' : 'Creating credit...',
            actionConfirmationMessage: `Successfully ${props.originalEntity?.id ? 'updated' : 'created'} credit`
        }),
        validate: async props  => {
            props.validation.failWithWarningIf(props.submittedEntity.creditAmount <= 0, 'Please specify a positive credit amount');
            props.validation.failWithWarningIf(props.submittedEntity.description === '', 'Please specify a description for this credit');
        },
        action: async props => {
            return await contactCreditSubmissionRequest.post({
                path: 'ContactCredit',
                postedData: {
                    ...props.submittedEntity,
                    contactId: contactId
                }
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: SaleContactDataGetContactDetailsQuery,
                queryContext: { contactId: contactId },
                update: contactDetails => {
                    return {
                        ...contactDetails,
                        credits: {
                            ...contactDetails.credits,
                            [props.responseData.id]: {
                                ...props.responseData
                            }
                        }
                    };
                }
            });

            onSuccess?.(props);
        }
    }
}

export const useContactCreditDataDeleteCreditAction = (): AgTechDataAction<ContactCredit, boolean> => {
    const contactCreditDeletionRequest = useAgTechHttpDeleteEntityRequest();
    const popupContext = useAgTechPopUpContext();

    return {
        name: 'DeleteCredit',
        getConfiguration: () => ({
            actionExecutionMessage: 'Deleting credit...',
            actionConfirmationMessage: `Successfully deleted credit`,
            showConfirmationMessage: true,
            actionConfirmationDelay: 300
        }),
        validate: async props => {
            props.validation.failWithWarningIf(
                props.submittedEntity.invoiceId !== undefined,
                'This credit has been used in a customer invoice and cannot be deleted.');
        },
        confirm: async props => {
            return await props.appContext.confirmations.askForConfirmation({
                content: {
                    header: 'Are you sure you want to delete this credit?',
                    confirmationButtonText: 'Delete Credit'
                },
                actions: DefaultAgTechConfirmationActions
            });
        },
        action: async props => {
            return await contactCreditDeletionRequest.deleteEntity({
                controller: 'ContactCredit',
                entityId: props.submittedEntity.id,
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: SaleContactDataGetContactDetailsQuery,
                queryContext: { contactId: props.submittedEntity.contactId },
                update: contactDetails => {
                    const updatedCredits = Object.values(contactDetails.credits)
                        .filter(credit => credit.id !== props.submittedEntity.id);

                    return {
                        ...contactDetails,
                        credits: Index(updatedCredits, credit => credit.id)
                    }
                }
            });

            popupContext.closePopUp();
        }
    }
}